import { useEffect, useState } from 'react';
import { useAppSelector } from '../store';
import httpClient from '../api/httpClient';

export type FloatingAssistAction = 'play' | 'quick';

export interface IngestMeta {
  combined_hash_current?: string | null;
  combined_hash_synced?: string | null;
  window_unchanged?: boolean;
  preparation_focus_hash?: string | null;
  preparation_audio_url?: string | null;
  auto_play_preparation_audio?: boolean;
  ingest_sync_pending?: boolean;
}

export interface FloatingAssistHints {
  play: string;
  quick: string;
  playAudioUrl: string;
  insightsCacheKey: string | null;
  autoPlayPreparationAudio: boolean;
  ingestSyncPending: boolean;
  isPersonalizedAudio: boolean;
}

const FALLBACK_AUDIO_URL = '/Luci_intro.mp3';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 20;

const PLAY_HINT_TEXT =
  'Listen to what your focus recommendations are for the next interviews';

const DEFAULT_HINTS: FloatingAssistHints = {
  play: PLAY_HINT_TEXT,
  quick: 'Start a practice interview on your current resume and preference',
  playAudioUrl: FALLBACK_AUDIO_URL,
  insightsCacheKey: null,
  autoPlayPreparationAudio: false,
  ingestSyncPending: false,
  isPersonalizedAudio: false,
};

const hintsFromInsightsResponse = (rawData: {
  insights?: Record<string, unknown>;
  ingest_meta?: IngestMeta;
}): FloatingAssistHints => {
  const insightsPayload = rawData.insights || rawData;
  const ingestMeta = rawData.ingest_meta;
  const preparationAudioUrl =
    (ingestMeta?.preparation_audio_url as string | undefined) ||
    (insightsPayload.preparation_audio_url as string | undefined) ||
    FALLBACK_AUDIO_URL;
  const isPersonalized =
    Boolean(preparationAudioUrl) && preparationAudioUrl !== FALLBACK_AUDIO_URL;
  const cacheKey =
    (ingestMeta?.preparation_focus_hash as string | undefined) ||
    (insightsPayload.preparation_focus_hash as string | undefined) ||
    (insightsPayload.last_updated as string | undefined) ||
    null;

  return {
    play: PLAY_HINT_TEXT,
    quick: DEFAULT_HINTS.quick,
    playAudioUrl: preparationAudioUrl,
    insightsCacheKey: cacheKey,
    autoPlayPreparationAudio: Boolean(ingestMeta?.auto_play_preparation_audio),
    ingestSyncPending: Boolean(ingestMeta?.ingest_sync_pending),
    isPersonalizedAudio: isPersonalized,
  };
};

export const useFloatingAssistHints = (): FloatingAssistHints => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [hints, setHints] = useState<FloatingAssistHints>(DEFAULT_HINTS);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setHints(DEFAULT_HINTS);
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollAttempts = 0;

    const fetchInsights = async (): Promise<FloatingAssistHints | null> => {
      const response = await httpClient.get(`ai/insights/${user.id}`);
      if (!response.data?.success) {
        return null;
      }
      return hintsFromInsightsResponse(response.data);
    };

    const loadInsights = async () => {
      try {
        const next = await fetchInsights();
        if (cancelled || !next) {
          return;
        }

        setHints(next);

        const shouldPoll =
          next.ingestSyncPending &&
          !next.autoPlayPreparationAudio &&
          pollAttempts < POLL_MAX_ATTEMPTS;

        if (shouldPoll) {
          pollAttempts += 1;
          pollTimer = setTimeout(() => {
            void loadInsights();
          }, POLL_INTERVAL_MS);
        }
      } catch {
        if (!cancelled) {
          setHints(DEFAULT_HINTS);
        }
      }
    };

    pollAttempts = 0;
    void loadInsights();

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
    };
  }, [isAuthenticated, user?.id]);

  return hints;
};

export const ackPreparationAudioPlayback = async (userId: string): Promise<void> => {
  await httpClient.post(`ai/insights/${userId}/preparation-audio/ack`);
};
